import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formOptions } from '../data/mock';
import { ArrowLeft, CheckCircle, Loader2, Shield, Sparkles } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_URL = 'https://wa.me/919515903777?text=Hi%20I%20just%20submitted%20my%20details%20on%20TCUBE%20Fitness%20website.%20I%20am%20interested%20in%20the%20consultation%20and%20would%20like%20to%20know%20more.';

const SubmitDetailsPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    age: '',
    gender: '',
    country: '',
    mobile: '',
    whatsapp: '',
    email: '',
    healthConditions: [],
    onMedications: '',
    medicationsDetails: '',
    primaryGoal: '',
    triedBefore: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleConditionChange = (value) => {
    setFormData(prev => {
      const currentConditions = prev.healthConditions;
      const isCurrentlyChecked = currentConditions.includes(value);
      
      // If clicking "None"
      if (value === 'none') {
        if (isCurrentlyChecked) {
          // Uncheck "None"
          return { ...prev, healthConditions: [] };
        } else {
          // Check "None" - clear all others
          return { ...prev, healthConditions: ['none'] };
        }
      }
      
      // If clicking any other condition
      if (isCurrentlyChecked) {
        // Uncheck this condition
        return { ...prev, healthConditions: currentConditions.filter(c => c !== value) };
      } else {
        // Check this condition - also remove "None" if present
        return { ...prev, healthConditions: [...currentConditions.filter(c => c !== 'none'), value] };
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.age) newErrors.age = 'Age is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    if (!formData.whatsapp.trim()) newErrors.whatsapp = 'WhatsApp number is required';
    if (!formData.email.trim()) newErrors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        full_name: formData.fullName,
        age: parseInt(formData.age),
        gender: formData.gender,
        country: formData.country,
        mobile: formData.mobile,
        whatsapp: formData.whatsapp,
        email: formData.email || null,
        health_conditions: formData.healthConditions,
        on_medications: formData.onMedications || null,
        medications_details: formData.medicationsDetails || null,
        primary_goal: formData.primaryGoal || null,
        tried_before: formData.triedBefore || null,
        additional_info: formData.additionalInfo || null
      };
      
      await axios.post(`${API}/consultations`, payload);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-redirect to WhatsApp after form submission
  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        window.open(WHATSAPP_URL, '_blank');
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="pt-32 pb-20 relative overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-emerald-600/20 to-teal-600/20 flex items-center justify-center mx-auto mb-8 border border-emerald-500/30">
                <CheckCircle size={48} className="text-emerald-400" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Thank You!</h1>
              <p className="text-xl text-slate-300 mb-2">
                Your details have been submitted successfully.
              </p>
              <p className="text-lg text-emerald-400 font-medium mb-8">
                For a faster response, connect with us on WhatsApp.
              </p>
              
              {/* WhatsApp CTA Button */}
              <a 
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-5 rounded-full font-bold text-xl transition-all shadow-lg shadow-emerald-500/30 hover:scale-105 mb-4"
                data-testid="whatsapp-cta-btn"
              >
                <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Chat on WhatsApp Now
              </a>
              
              <p className="text-slate-500 text-sm mb-10">
                Redirecting you to WhatsApp automatically...
              </p>

              <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl p-8 mb-10 border border-slate-800">
                <img 
                  src="https://customer-assets.emergentagent.com/job_8b2c67d7-f3f8-4dd8-ad33-921e8e7f1a96/artifacts/s6b7c1yc_transprant.png"
                  alt="T Cube Fitness Logo"
                  className="h-28 w-auto mx-auto mb-4"
                  width="112"
                  height="112"
                  loading="lazy"
                  decoding="async"
                />
                <p className="text-2xl font-bold text-white mb-2">TCUBE FITNESS</p>
                <p className="font-semibold">
                  <span className="text-blue-400">Treat</span>
                  <span className="text-slate-500 mx-2">•</span>
                  <span className="text-orange-400">Train</span>
                  <span className="text-slate-500 mx-2">•</span>
                  <span className="text-emerald-400">Transform</span>
                </p>
              </div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-blue-400 font-medium hover:text-blue-300 transition-colors"
              >
                <ArrowLeft size={18} />
                Back to Home
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-28 pb-20 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-10">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-400 mb-6 transition-colors"
              >
                <ArrowLeft size={18} />
                Back to Home
              </Link>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Book Your Consultation
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Submit Your Details</h1>
              <p className="text-lg bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent font-semibold mb-4">
                Doctor-Led Health, Nutrition & Fitness Guidance
              </p>
              <p className="text-slate-400">
                To help us understand your health needs better, please share a few basic details below.
                Our medical and nutrition team will review your information and guide you on the next steps.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Form Title */}
              <div className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 rounded-2xl p-4 text-center border border-emerald-500/20">
                <p className="font-semibold text-white">
                  Start Your Journey: <span className="text-emerald-400">Treat • Train • Transform</span>
                </p>
              </div>

              {/* Basic Information */}
              <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-slate-800">
                <h3 className="text-xl font-bold text-white mb-6">Basic Information</h3>
                <div className="space-y-5">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Full Name <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-slate-800/50 border ${errors.fullName ? 'border-rose-500' : 'border-slate-700'} rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                      placeholder="Enter your full name"
                    />
                    {errors.fullName && <p className="text-rose-400 text-sm mt-1">{errors.fullName}</p>}
                  </div>

                  {/* Age & Gender Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Age <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-slate-800/50 border ${errors.age ? 'border-rose-500' : 'border-slate-700'} rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                        placeholder="Your age"
                        min="1"
                        max="120"
                      />
                      {errors.age && <p className="text-rose-400 text-sm mt-1">{errors.age}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Gender <span className="text-rose-400">*</span>
                      </label>
                      <Select onValueChange={(value) => handleSelectChange('gender', value)}>
                        <SelectTrigger className={`h-12 bg-slate-800/50 border ${errors.gender ? 'border-rose-500' : 'border-slate-700'} rounded-xl text-white`}>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {formOptions.genderOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-700">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.gender && <p className="text-rose-400 text-sm mt-1">{errors.gender}</p>}
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Country <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-slate-800/50 border ${errors.country ? 'border-rose-500' : 'border-slate-700'} rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                      placeholder="Your country"
                    />
                    {errors.country && <p className="text-rose-400 text-sm mt-1">{errors.country}</p>}
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Mobile Number <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-slate-800/50 border ${errors.mobile ? 'border-rose-500' : 'border-slate-700'} rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                      placeholder="Mobile number with country code"
                    />
                    {errors.mobile && <p className="text-rose-400 text-sm mt-1">{errors.mobile}</p>}
                  </div>

                  {/* WhatsApp Number */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      WhatsApp Number <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-slate-800/50 border ${errors.whatsapp ? 'border-rose-500' : 'border-slate-700'} rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                      placeholder="WhatsApp number with country code"
                    />
                    {errors.whatsapp && <p className="text-rose-400 text-sm mt-1">{errors.whatsapp}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-slate-800/50 border ${errors.email ? 'border-rose-500' : 'border-slate-700'} rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-rose-400 text-sm mt-1">{errors.email}</p>}
                  </div>
                </div>
              </div>

              {/* Health Information */}
              <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-slate-800">
                <h3 className="text-xl font-bold text-white mb-6">Health Information</h3>
                <div className="space-y-6">
                  {/* Health Conditions */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Do you have any of the following conditions?
                    </label>
                    <p className="text-xs text-slate-500 mb-3">(Select all that apply)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {formOptions.healthConditions.map(condition => {
                        const isChecked = formData.healthConditions.includes(condition.value);
                        return (
                          <label 
                            key={condition.value} 
                            className={`flex items-center space-x-3 p-3 rounded-xl border transition-all cursor-pointer select-none ${
                              isChecked 
                                ? 'bg-blue-900/30 border-blue-500/50' 
                                : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleConditionChange(condition.value)}
                              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                            />
                            <span className="text-slate-300 flex-1">
                              {condition.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Medications */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Are you currently on any long-term medications?
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-800/30 rounded-xl border border-slate-700/50">
                        <input
                          type="radio"
                          name="onMedications"
                          value="yes"
                          checked={formData.onMedications === 'yes'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 bg-slate-800 border-slate-600"
                        />
                        <span className="text-slate-300">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-800/30 rounded-xl border border-slate-700/50">
                        <input
                          type="radio"
                          name="onMedications"
                          value="no"
                          checked={formData.onMedications === 'no'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 bg-slate-800 border-slate-600"
                        />
                        <span className="text-slate-300">No</span>
                      </label>
                    </div>
                    {formData.onMedications === 'yes' && (
                      <input
                        type="text"
                        name="medicationsDetails"
                        value={formData.medicationsDetails}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all mt-3"
                        placeholder="Please specify your medications"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Fitness & Nutrition Goals */}
              <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-slate-800">
                <h3 className="text-xl font-bold text-white mb-6">Fitness & Nutrition Goals</h3>
                <div className="space-y-6">
                  {/* Primary Goal */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      What is your primary goal?
                    </label>
                    <Select onValueChange={(value) => handleSelectChange('primaryGoal', value)}>
                      <SelectTrigger className="h-12 bg-slate-800/50 border border-slate-700 rounded-xl text-white">
                        <SelectValue placeholder="Select your primary goal" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {formOptions.goals.map(goal => (
                          <SelectItem key={goal.value} value={goal.value} className="text-white hover:bg-slate-700">
                            {goal.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tried Before */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Have you tried fitness or diet programs before?
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-800/30 rounded-xl border border-slate-700/50">
                        <input
                          type="radio"
                          name="triedBefore"
                          value="yes"
                          checked={formData.triedBefore === 'yes'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 bg-slate-800 border-slate-600"
                        />
                        <span className="text-slate-300">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-800/30 rounded-xl border border-slate-700/50">
                        <input
                          type="radio"
                          name="triedBefore"
                          value="no"
                          checked={formData.triedBefore === 'no'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 bg-slate-800 border-slate-600"
                        />
                        <span className="text-slate-300">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-slate-800">
                <h3 className="text-xl font-bold text-white mb-6">Additional Information (Optional)</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Anything you would like our team to know?
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all min-h-[120px] resize-none"
                    placeholder="Share any additional information that might help us understand your needs better..."
                  />
                </div>
              </div>

              {/* Consent & No-Spam Policy */}
              <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-slate-800">
                <div className="space-y-6">
                  {/* Consent to Contact */}
                  <div className="bg-blue-900/20 rounded-xl p-5 border border-blue-500/20">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <CheckCircle size={18} className="text-blue-400" />
                      Consent to Contact
                    </h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      By submitting this form, you agree to be contacted by TCUBE Fitness regarding your consultation, fitness program, nutrition plan, fertility health program, and lifestyle management services.
                    </p>
                  </div>

                  {/* No-Spam Policy */}
                  <div className="bg-emerald-900/20 rounded-xl p-5 border border-emerald-500/20">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <Shield size={18} className="text-emerald-400" />
                      No-Spam Policy
                    </h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      At TCUBE Fitness, we follow a strict no-spam policy. After your free in-depth consultation call with our doctor, we will send only one follow-up message. We do not send repeated or unnecessary messages. You are always welcome to reach out to us whenever you are ready to join the program.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-700 text-white px-12 py-4 rounded-full font-semibold hover:opacity-90 transition-all shadow-lg shadow-blue-500/30 text-lg disabled:opacity-70 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit My Details'
                  )}
                </button>
              </div>

              {/* What Happens Next */}
              <div className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 rounded-2xl p-6 border border-emerald-500/20">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Shield size={18} className="text-emerald-400" />
                  What Happens Next?
                </h4>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• Your details will be reviewed by our medical & nutrition team</li>
                  <li>• We will contact you to discuss appropriate next steps</li>
                  <li>• No generic plans. No automated advice.</li>
                </ul>
              </div>

              {/* Disclaimer */}
              <p className="text-center text-sm text-slate-500 bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                <strong className="text-slate-400">Disclaimer:</strong> The information submitted is used only for health & fitness guidance.
                This does not replace in-person medical consultation, diagnosis, or treatment.
              </p>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SubmitDetailsPage;
